/* eslint-disable camelcase */
import React, { useCallback, useEffect, useMemo, useState, ElementType } from 'react';

import {
  Order_By,
  useGetAllCategoriesQuery,
  useGetMyRestaurantQuery,
  useGetRestaurantCategoryDishesQuery,
  useInsertDishOneMutation,
} from '../utils/__generated__/graphql';
import {
  Button,
  useDisclosure,
  useToast,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { nhost } from '../lib/nhost';
import { CategorizedFoods, PageHeader, ModernCard, LoadingSpinner } from './shared';
import { useUserId } from '@nhost/react';
import { DishModal } from './shared/DishModal';
import { compressImage } from 'src/utils';

const Menu = () => {
  const toast = useToast();


  const userId = useUserId();

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: restaurantData } = useGetMyRestaurantQuery({
    variables: { id: userId },
    skip: !userId,
    fetchPolicy: 'cache-first',
  });

  const restaurantId = restaurantData?.restaurant?.[0]?.id;

  const {
    data: restaurantCategoryDishesData,
    loading: dishesLoading,
    error: dishesError,
    refetch: refetchDishes,
  } = useGetRestaurantCategoryDishesQuery({
    variables: {
      category_order_by: [{ name: Order_By.Asc }],
      dish_order_by: [{ name: Order_By.Asc }],
      id: restaurantId,
    },
    skip: !restaurantId,
  });

  const {
    data: categoriesData,
    loading: isCategoriesDataLoading,
    error: getAllCategoriesError,
  } = useGetAllCategoriesQuery();

  const [createDish, { loading: isCreatingDish, error: insertDishOneError }] = useInsertDishOneMutation({
    onCompleted: () => {
      refetchDishes();
      toast({
        title: 'Dish Created',
        description: 'New dish has been added successfully',
        status: 'success',
        duration: 3000,
      });
    },
  });

  const options = useMemo(
    () =>
      categoriesData?.category.map((category) => ({
        value: category.id,
        label: category.name,
      })) ?? [],
    [categoriesData]
  );

  useEffect(() => {
    if (options.length > 0 && !selectedCategory) {
      setSelectedCategory(options[0].value);
    }
  }, [options, selectedCategory]);



  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        const compressedFile = await compressImage(file);
        const result = await nhost.storage.upload({ file: compressedFile });

        if (result?.fileMetadata?.id) {
          toast({
            title: 'Image Uploaded',
            status: 'success',
            duration: 2000,
          });
        }
      } catch (error: any) {
        toast({
          title: 'Upload Failed',
          description: error.message,
          status: 'error',
          duration: 3000,
        });
      }
    },
    [toast]
  );

  const textColor = useColorModeValue('gray.600', 'gray.300');

  if (isCategoriesDataLoading || dishesLoading) return <LoadingSpinner text="Loading menu..." />;

  if (dishesError || insertDishOneError || getAllCategoriesError) {
    const errorMessage = [getAllCategoriesError?.message, insertDishOneError?.message, dishesError?.message]
      .filter(Boolean)
      .join(' ');

    toast({
      title: 'Error',
      description: errorMessage,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

    return (
      <ModernCard p={8}>
        <VStack spacing={4} textAlign="center">
          <Text fontSize="lg" fontWeight="semibold" color="red.500">
            Error Loading Menu
          </Text>
          <Text color={textColor}>{errorMessage}</Text>
        </VStack>
      </ModernCard>
    );
  }


  const handleCreateDish = async (formData: any) => {
    try {
      await createDish({
        variables: {
          object: {
            name: formData.name,
            price: formData.price,
            restaurantId: restaurantId,
            categoryId: formData.categoryId,
            image: formData.image,
          },
        },
      });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const totalDishes =
    restaurantCategoryDishesData?.category?.reduce(
      (total, category) => total + (category?.dishes_aggregate?.aggregate?.count || 0),
      0
    ) || 0;

  return (
    <>
      <PageHeader
        title="Menu Management"
        subtitle="Manage your restaurant menu items and categories"
        badge={
          <HStack spacing={2}>
            <Badge colorScheme="green" fontSize="sm" borderRadius="full" px={3} py={1}>
              {restaurantCategoryDishesData?.category?.length || 0} Categories
            </Badge>
            <Badge colorScheme="teal" fontSize="sm" borderRadius="full" px={3} py={1}>
              {totalDishes} Dishes
            </Badge>
          </HStack>
        }
        actions={
          <Button
            leftIcon={<Icon as={FiPlus as ElementType} />}
            onClick={onOpen}
            colorScheme="brand"
            size={{ base: 'md', md: 'lg' }}
            _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Add New Dish</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Add Dish</Text>
          </Button>
        }
      />

      <CategorizedFoods dishesData={restaurantCategoryDishesData} refetch={refetchDishes} />

      <DishModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleCreateDish}
        categories={options}
        isLoading={isCreatingDish}
        handleFileChange={handleFileChange}
      />
    </>
  );
};

export default Menu;
